import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const AdminActivityAudit = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedAction, setSelectedAction] = useState('all');
  const [timeRange, setTimeRange] = useState('24h');

  // Enhanced admin activity logs with proper database structure
  const [activities, setActivities] = useState([
    {
      id: 1,
      adminName: "John Mitchell",
      adminAvatar: "https://images.unsplash.com/photo-1560250097-0b93528c311a?w=150",
      action: "tenant_update",
      description: "Updated billing plan for TechCorp Solutions from Pro to Enterprise",
      date: "2025-01-26T11:45:00Z",
      tenant: "TechCorp Solutions",
      impact: "high",
      ipAddress: "192.168.1.100",
      userAgent: "Chrome 120.0.0.0",
      details: {
        oldPlan: "Pro ($99/month)",
        newPlan: "Enterprise ($299/month)",
        effectiveDate: "2025-02-01"
      }
    },
    {
      id: 2,
      adminName: "Sarah Johnson",
      adminAvatar: "https://images.unsplash.com/photo-1494790108755-2616b612b786?w=150",
      action: "ticket_assignment",
      description: "Assigned critical ticket TKT-2025-001 to Michael Chen",
      date: "2025-01-26T10:30:00Z",
      tenant: "TechCorp Solutions",
      impact: "medium",
      ipAddress: "192.168.1.101",
      userAgent: "Firefox 121.0.0.0",
      details: {
        ticketId: "TKT-2025-001",
        assignedTo: "Michael Chen",
        priority: "Critical",
        previousAssignee: "Unassigned"
      }
    },
    {
      id: 3,
      adminName: "Michael Chen",
      adminAvatar: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150",
      action: "system_config",
      description: "Updated API rate limits for enterprise tier",
      date: "2025-01-26T09:15:00Z",
      tenant: "System Wide",
      impact: "high",
      ipAddress: "192.168.1.102",
      userAgent: "Chrome 120.0.0.0",
      details: {
        oldLimit: "1000 requests/hour",
        newLimit: "5000 requests/hour",
        affectedTenants: 23
      }
    },
    {
      id: 4,
      adminName: "Emily Rodriguez",
      adminAvatar: "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=150",
      action: "ticket_response",
      description: "Added response to ticket TKT-2025-002 regarding performance issues",
      date: "2025-01-26T08:00:00Z",
      tenant: "DataFlow Inc",
      impact: "low",
      ipAddress: "192.168.1.103",
      userAgent: "Chrome 120.0.0.0",
      details: {
        ticketId: "TKT-2025-002",
        responseType: "Solution Provided",
        statusChanged: "In Progress"
      }
    },
    {
      id: 5,
      adminName: "David Kim",
      adminAvatar: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=150",
      action: "tenant_creation",
      description: "Created new tenant account for DataFlow Inc",
      date: "2025-01-25T16:45:00Z",
      tenant: "DataFlow Inc",
      impact: "medium",
      ipAddress: "192.168.1.104",
      userAgent: "Safari 17.0.0.0",
      details: {
        plan: "Pro ($99/month)",
        users: 25,
        trialPeriod: "14 days"
      }
    },
    {
      id: 6,
      adminName: "Lisa Thompson",
      adminAvatar: "https://images.unsplash.com/photo-1544005313-94ddf0286df2?w=150",
      action: "ticket_closure",
      description: "Resolved and closed ticket TKT-2025-004 after email notification fix",
      date: "2025-01-25T14:20:00Z",
      tenant: "CloudSync Ltd",
      impact: "medium",
      ipAddress: "192.168.1.105",
      userAgent: "Chrome 120.0.0.0",
      details: {
        ticketId: "TKT-2025-004",
        resolutionTime: "2.5 hours",
        satisfactionScore: "5/5",
        slaStatus: "Met"
      }
    }
  ]);

  const addActivity = (adminName, action, description, tenant) => {
    const newActivity = {
      id: activities.length + 1,
      adminName,
      adminAvatar: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=150",
      action,
      description,
      date: new Date().toISOString(),
      tenant,
      impact: "medium",
      ipAddress: "192.168.1.100",
      userAgent: "Chrome 120.0.0.0",
      details: {}
    };
    
    setActivities([newActivity, ...activities]);
  };

  const getActionColor = (action) => {
    switch (action) {
      case 'tenant_update': return 'text-primary bg-primary/10 border-primary/20';
      case 'ticket_assignment': return 'text-warning bg-warning/10 border-warning/20';
      case 'ticket_response': return 'text-success bg-success/10 border-success/20';
      case 'ticket_closure': return 'text-success bg-success/10 border-success/20';
      case 'system_config': return 'text-error bg-error/10 border-error/20';
      case 'tenant_creation': return 'text-secondary bg-secondary/10 border-secondary/20';
      default: return 'text-text-secondary bg-muted border-border';
    }
  };

  const getImpactColor = (impact) => {
    switch (impact) {
      case 'high': return 'text-error';
      case 'medium': return 'text-warning';
      case 'low': return 'text-success';
      default: return 'text-text-secondary';
    }
  };

  const getActionIcon = (action) => {
    switch (action) {
      case 'tenant_update': return 'Edit';
      case 'ticket_assignment': return 'UserCheck';
      case 'ticket_response': return 'MessageSquare';
      case 'ticket_closure': return 'CheckCircle2';
      case 'system_config': return 'Settings';
      case 'tenant_creation': return 'UserPlus';
      default: return 'Activity';
    }
  };

  const formatTimeAgo = (dateString) => {
    const now = new Date();
    const date = new Date(dateString);
    const diffInMinutes = Math.floor((now - date) / (1000 * 60));
    
    if (diffInMinutes < 1) return 'Just now';
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
    if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)}h ago`;
    return `${Math.floor(diffInMinutes / 1440)}d ago`;
  };

  const filteredActivities = activities.filter(activity => {
    const matchesSearch = activity.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         activity.adminName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         activity.tenant.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesAction = selectedAction === 'all' || activity.action === selectedAction;
    
    return matchesSearch && matchesAction;
  });

  return (
    <div className="bg-card border border-border rounded-lg shadow-elevation-1 dashboard-fade-in">
      <div className="p-6 border-b border-border">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-accent/10 rounded-lg flex items-center justify-center">
              <Icon name="Shield" size={20} className="text-accent" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-text-primary">Admin Activity Logs</h3>
              <p className="text-sm text-text-secondary">Track administrative actions and system changes</p>
            </div>
          </div>
          
          <div className="flex items-center space-x-2">
            <Button variant="outline" size="sm" iconName="Filter">
              Advanced Filter
            </Button>
            <Button variant="outline" size="sm" iconName="Download">
              Export Log
            </Button>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <Input
              type="search"
              placeholder="Search activities, admins, or tenants..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full"
            />
          </div>
          
          <div className="flex gap-2">
            <select
              value={selectedAction}
              onChange={(e) => setSelectedAction(e.target.value)}
              className="px-3 py-2 bg-input border border-border rounded-md text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-primary"
            >
              <option value="all">All Actions</option>
              <option value="tenant_update">Tenant Updates</option>
              <option value="ticket_assignment">Ticket Assignments</option>
              <option value="ticket_response">Ticket Responses</option>
              <option value="ticket_closure">Ticket Closures</option>
              <option value="system_config">System Config</option>
              <option value="tenant_creation">Tenant Creation</option>
            </select>
            
            <select
              value={timeRange}
              onChange={(e) => setTimeRange(e.target.value)}
              className="px-3 py-2 bg-input border border-border rounded-md text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-primary"
            >
              <option value="1h">Last Hour</option>
              <option value="24h">Last 24 Hours</option>
              <option value="7d">Last 7 Days</option>
              <option value="30d">Last 30 Days</option>
            </select>
          </div>
        </div>
      </div>

      {/* Enhanced Table View for Admin Activity Logs */}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-muted/30">
            <tr>
              <th className="text-left p-4 text-sm font-medium text-text-secondary">Admin Name</th>
              <th className="text-left p-4 text-sm font-medium text-text-secondary">Action</th>
              <th className="text-left p-4 text-sm font-medium text-text-secondary">Description</th>
              <th className="text-left p-4 text-sm font-medium text-text-secondary">Date</th>
              <th className="text-left p-4 text-sm font-medium text-text-secondary">Tenant</th>
              <th className="text-left p-4 text-sm font-medium text-text-secondary">Impact</th>
              <th className="text-left p-4 text-sm font-medium text-text-secondary">Details</th>
            </tr>
          </thead>
          <tbody>
            {filteredActivities.map((activity) => (
              <tr key={activity.id} className="border-b border-border hover:bg-muted/20 transition-colors duration-150">
                <td className="p-4">
                  <div className="flex items-center space-x-3">
                    <img
                      src={activity.adminAvatar}
                      alt={activity.adminName}
                      className="w-8 h-8 rounded-full object-cover"
                    />
                    <span className="text-sm font-medium text-text-primary">{activity.adminName}</span>
                  </div>
                </td>
                
                <td className="p-4">
                  <div className="flex items-center space-x-2">
                    <div className="w-8 h-8 bg-muted rounded-full flex items-center justify-center">
                      <Icon name={getActionIcon(activity.action)} size={14} className="text-text-primary" />
                    </div>
                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${getActionColor(activity.action)}`}>
                      {activity.action.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                    </span>
                  </div>
                </td>
                
                <td className="p-4">
                  <p className="text-sm text-text-primary max-w-xs truncate" title={activity.description}>
                    {activity.description}
                  </p>
                </td>
                
                <td className="p-4">
                  <div className="text-sm text-text-secondary">
                    <div>{formatTimeAgo(activity.date)}</div>
                    <div className="text-xs text-text-muted">
                      {new Date(activity.date).toLocaleDateString()}
                    </div>
                  </div>
                </td>
                
                <td className="p-4">
                  <span className="text-sm font-medium text-text-primary">{activity.tenant}</span>
                </td>
                
                <td className="p-4">
                  <div className="flex items-center space-x-1">
                    <Icon name="AlertCircle" size={12} className={getImpactColor(activity.impact)} />
                    <span className={`text-xs font-medium ${getImpactColor(activity.impact)}`}>
                      {activity.impact.toUpperCase()}
                    </span>
                  </div>
                </td>
                
                <td className="p-4">
                  <Button variant="ghost" size="sm" className="text-xs">
                    View Details
                    <Icon name="ChevronRight" size={12} className="ml-1" />
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {filteredActivities.length === 0 && (
        <div className="p-8 text-center">
          <Icon name="Search" size={48} className="text-text-secondary mx-auto mb-4" />
          <p className="text-text-secondary">No activities found matching your criteria</p>
        </div>
      )}

      <div className="p-4 border-t border-border bg-muted/20">
        <div className="flex items-center justify-between text-sm text-text-secondary">
          <span>Showing {filteredActivities.length} of {activities.length} activities</span>
          <div className="flex items-center space-x-2">
            <Button variant="ghost" size="sm">
              Load More
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminActivityAudit;